import { QueryCache, QueryClient } from '@tanstack/react-query';
import { errorReportingService } from '../../shared/src/services/errorReportingService';
import { useAuthStore } from '@tickitacka/shared';
import { useWorkspaceStore } from './features/workspace/hooks';

// 순환 참조를 안전하게 처리
type SafeValue =
    | string
    | number
    | boolean
    | null
    | undefined
    | SafeValue[]
    | { [key: string]: SafeValue };

function extractSafeData(obj: unknown, maxDepth = 2): SafeValue {
    const seen = new WeakSet<object>();

    const extract = (value: unknown, depth: number): SafeValue => {
        if (value === null) return null;
        if (value === undefined) return undefined;

        const type = typeof value;

        if (type === 'string') {
            // 문자열 길이 제한
            if ((value as string).length > 200) {
                return (value as string).substring(0, 200) + '...[truncated]';
            }
            return value as string;
        }

        if (type === 'number') return value as number;
        if (type === 'boolean') return value as boolean;

        if (type === 'function') return '[Function]';
        if (depth >= maxDepth) return '[Max Depth]';

        if (type === 'object') {
            if (seen.has(value as object)) return '[Circular Reference]';
            seen.add(value as object);

            // 특수 객체 처리
            if (value instanceof Date) return value.toISOString();
            if (value instanceof Error) {
                return {
                    _type: 'Error',
                    name: value.name,
                    message: value.message,
                };
            }

            // 배열
            if (Array.isArray(value)) {
                const limited = value.slice(0, 20); // 최대 20개
                return limited.map(item => extract(item, depth + 1));
            }

            // 일반 객체
            const result: Record<string, SafeValue> = {};
            const keys = Object.keys(value as object).slice(0, 30); // 최대 30개 키

            for (const key of keys) {
                try {
                    result[key] = extract(
                        (value as Record<string, unknown>)[key],
                        depth + 1
                    );
                } catch (e) {
                    result[key] = '[Error]';
                }
            }
            return result;
        }

        return '[Unknown]';
    };

    return extract(obj, 0);
}

/**
 * QueryKey에서 feature module 이름을 추출합니다
 * 예: ['oneonone', 'ai', 'analysis'] -> 'query/oneonone/ai'
 */
function extractFeatureModule(
    queryKey: unknown,
    type: 'query' | 'mutation'
): string {
    if (Array.isArray(queryKey) && queryKey.length > 0) {
        const parts = queryKey
            .slice(0, 3) // 최대 3단계까지만
            .map(k => {
                if (typeof k === 'string') return k;
                if (typeof k === 'number') return String(k);
                return null;
            })
            .filter((k): k is string => k !== null);

        if (parts.length > 0) {
            return `${type}/${parts.join('/')}`;
        }
    }

    return `main-app-react-query-${type}`;
}

/**
 * Query/Mutation 실패 횟수를 기반으로 심각도를 결정합니다
 */
function determineSeverity(failureCount: number): 'critical' | 'error' | 'warning' {
    if (failureCount >= 3) return 'critical'; // 3번 이상 실패 시 심각
    if (failureCount >= 1) return 'error';    // 재시도 중 실패
    return 'warning';                          // 첫 실패
}

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60 * 5,
            gcTime: 1000 * 60 * 30,
            retry: 1,
            refetchOnWindowFocus: true,
        },

        mutations: {
            onError: (error, variables, context) => {
                const { user } = useAuthStore.getState();
                const { currentWorkspace } = useWorkspaceStore.getState();

                const safeVariables = extractSafeData(variables);

                void errorReportingService.reportError(
                    user?.id,
                    currentWorkspace?.id,
                    error,
                    'main-app-react-query-mutation', // mutation은 queryKey가 없어서 일반적으로 유지
                    {
                        variables: safeVariables,
                        hasContext: !!context,
                        operationType: 'mutation',
                    },
                    'error' // mutation 실패는 기본적으로 error 수준
                );
            },
        },
    },
    queryCache: new QueryCache({
        onError: (error, query) => {
            const { user } = useAuthStore.getState();
            const { currentWorkspace } = useWorkspaceStore.getState();

            // 안전하게 queryKey만 문자열로 변환
            const safeQueryKey = Array.isArray(query.queryKey)
                ? query.queryKey
                      .map(k => {
                          if (typeof k === 'string' || typeof k === 'number') {
                              return String(k);
                          }
                          try {
                              return JSON.stringify(k);
                          } catch (e) {
                              return '[Complex Object]';
                          }
                      })
                      .join('/')
                : typeof query.queryKey === 'string' ||
                    typeof query.queryKey === 'number'
                  ? String(query.queryKey)
                  : '[Complex QueryKey]';

            // queryKey 기반으로 구체적인 feature module 추출
            const featureModule = extractFeatureModule(query.queryKey, 'query');

            // 실패 횟수 기반 심각도 결정
            const severity = determineSeverity(query.state.fetchFailureCount);

            void errorReportingService.reportError(
                user?.id,
                currentWorkspace?.id,
                error,
                featureModule,
                {
                    queryKey: safeQueryKey,
                    queryHash: query.queryHash,
                    failureCount: query.state.fetchFailureCount,
                    operationType: 'query',
                },
                severity
            );
        },
    }),
});
